/**
 * AbsorptionTimingCharts — «Графики» режима «Поглощение»
 * =========================================================
 *
 * Источники: /level-funnel/table (row.absorption — общая медиана/среднее по
 * уровню, без разбивки по бустерам) и /level-funnel/absorption-timing
 * (группы g0/1..N-1/N+, пороги звёзд timeLimitSec). Пороги звёзд считаются по
 * правилу ТЗ: 3★ = timeLimitSec-59, 2★ = timeLimitSec-29, 1★ = timeLimitSec —
 * рисуются ступенчато (type="stepAfter"), т.к. лимит меняется по уровням.
 */

import { levelAxisDomain } from './slice';
import React, { useMemo } from 'react';
import { ComposedChart, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { Tooltip } from '../../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../../ui/responsive-chart';
import { fmtSec, fmtPct } from './format';
import { levelFunnel as levelFunnelSpecs } from '@ggstat/chart-specs';

// Ряды, цвета и заголовки — общие с MCP (shared/chart-specs/specs/levelFunnel.json).
const { timingOverall: TIMING, timingByGroup, timingMedianBars, starShare: STAR_SHARE } = levelFunnelSpecs;
const GROUP_COLORS = levelFunnelSpecs.boosterGroups.colors;
const STAR_COLORS = STAR_SHARE.series.map((sr) => sr.color);
// Пороги звёзд — пунктирные ряды спецификации (t3/t2/t1)
const THRESHOLDS = TIMING.series.filter((sr) => sr.dashed);

function groupLabel(key, boosterN) {
  if (key === 'g0') return '0 бустеров';
  if (key === 'gMid') return `1–${boosterN - 1}`;
  return `${boosterN}+`;
}

/** Пороги звёзд по правилу ТЗ, из timeLimitSec (мода лимита на уровне). */
function starThresholds(timeLimitSec) {
  if (timeLimitSec === null || timeLimitSec === undefined) {
    return { t3: null, t2: null, t1: null };
  }
  return { t3: timeLimitSec - 59, t2: timeLimitSec - 29, t1: timeLimitSec };
}

function TimeTooltip({ active, payload, label, timingByLevel }) {
  if (!active || !payload?.length) return null;
  const timing = timingByLevel.get(label);
  return (
    <div className="bg-background border rounded-lg shadow-lg p-3 text-sm space-y-0.5">
      <p className="font-medium mb-1">Уровень {label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} style={{ color: entry.color }}>
          {entry.name}: {fmtSec(entry.value)}
        </p>
      ))}
      {timing?.timeLimits?.length > 1 && (
        <p className="text-xs text-muted-foreground">
          time_limit на уровне: {timing.timeLimits.map((t) => fmtSec(t)).join(' / ')}
        </p>
      )}
    </div>
  );
}

export function AbsorptionTimingCharts({ tableData, timingData, showAbs, boosterN }) {
  const tableRows = useMemo(() => tableData?.rows || [], [tableData]);
  const timingRows = useMemo(() => timingData?.rows || [], [timingData]);

  const timingByLevel = useMemo(() => {
    const map = new Map();
    timingRows.forEach((r) => map.set(r.level, r));
    return map;
  }, [timingRows]);

  const groupKeys = useMemo(
    () => (boosterN > 1 ? ['g0', 'gMid', 'gHigh'] : ['g0', 'gHigh']),
    [boosterN]
  );

  // График 4: общая медиана/среднее (из /table) + ступенчатые пороги звёзд.
  const overallData = useMemo(
    () =>
      tableRows.map((r) => {
        const timing = timingByLevel.get(r.level);
        const th = starThresholds(timing?.timeLimitSec);
        return {
          level: r.level,
          medianSec: r.absorption?.medianSec ?? null,
          avgSec: r.absorption?.avgSec ?? null,
          t3: th.t3,
          t2: th.t2,
          t1: th.t1
        };
      }),
    [tableRows, timingByLevel]
  );

  // График 5: медиана/среднее по группам бустеров.
  const byGroupData = useMemo(
    () =>
      timingRows.map((r) => {
        const point = { level: r.level };
        groupKeys.forEach((k) => {
          point[`${k}_median`] = r.groups?.[k]?.medianSec ?? null;
          point[`${k}_avg`] = r.groups?.[k]?.avgSec ?? null;
        });
        return point;
      }),
    [timingRows, groupKeys]
  );

  // График 6: бар медианного времени по группам + ступенчатые пороги звёзд.
  const barData = useMemo(
    () =>
      timingRows.map((r) => {
        const th = starThresholds(r.timeLimitSec);
        const point = { level: r.level, t3: th.t3, t2: th.t2, t1: th.t1 };
        groupKeys.forEach((k) => {
          point[`${k}_median`] = r.groups?.[k]?.medianSec ?? null;
        });
        return point;
      }),
    [timingRows, groupKeys]
  );

  // График 7: stacked 100% доли звёзд по группам, суммарно по всем уровням диапазона.
  const starShareData = useMemo(() => {
    const sums = {};
    groupKeys.forEach((k) => {
      sums[k] = [0, 0, 0, 0];
    });
    timingRows.forEach((r) => {
      groupKeys.forEach((k) => {
        const stars = r.groups?.[k]?.stars || [0, 0, 0, 0];
        stars.forEach((v, i) => {
          sums[k][i] += v || 0;
        });
      });
    });
    return groupKeys.map((k) => {
      const stars = sums[k];
      const total = stars.reduce((a, b) => a + b, 0);
      const row = { group: groupLabel(k, boosterN) };
      [0, 1, 2, 3].forEach((i) => {
        row[`star${i}`] = total > 0 ? (stars[i] / total) * 100 : 0;
        row[`star${i}__num`] = stars[i];
        row[`star${i}__den`] = total;
      });
      return row;
    });
  }, [timingRows, groupKeys, boosterN]);

  return (
    <div className="space-y-8">
      <section>
        <p className="text-sm font-medium mb-2">
          {TIMING.title}
        </p>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={overallData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="level" type="number" domain={levelAxisDomain(tableData)} allowDecimals={false} className="text-xs" />
            <YAxis className="text-xs" tickFormatter={(v) => fmtSec(v)} />
            <Tooltip content={<TimeTooltip timingByLevel={timingByLevel} />} />
            <Legend />
            {TIMING.series.map((sr) =>
              sr.dashed ? (
                <Line key={sr.key} type="stepAfter" dataKey={sr.key} name={sr.label} stroke={sr.color} strokeDasharray="4 2" dot={false} isAnimationActive={false} connectNulls />
              ) : (
                <Line key={sr.key} type="monotone" dataKey={sr.key} name={sr.label} stroke={sr.color} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
              )
            )}
          </LineChart>
        </ResponsiveContainer>
      </section>

      <section>
        <p className="text-sm font-medium mb-2">{timingByGroup.title}</p>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={byGroupData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="level" type="number" domain={levelAxisDomain(tableData)} allowDecimals={false} className="text-xs" />
            <YAxis className="text-xs" tickFormatter={(v) => fmtSec(v)} />
            <Tooltip content={<TimeTooltip timingByLevel={timingByLevel} />} />
            <Legend />
            {groupKeys.map((k) => (
              <Line
                key={`${k}_median`}
                type="monotone"
                dataKey={`${k}_median`}
                name={`Медиана · ${groupLabel(k, boosterN)}`}
                stroke={GROUP_COLORS[k]}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
                connectNulls
              />
            ))}
            {groupKeys.map((k) => (
              <Line
                key={`${k}_avg`}
                type="monotone"
                dataKey={`${k}_avg`}
                name={`Среднее · ${groupLabel(k, boosterN)}`}
                stroke={GROUP_COLORS[k]}
                strokeDasharray="4 2"
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </section>

      <section>
        <p className="text-sm font-medium mb-2">
          {timingMedianBars.title}
        </p>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={barData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="level" type="number" domain={levelAxisDomain(tableData)} allowDecimals={false} className="text-xs" />
            <YAxis className="text-xs" tickFormatter={(v) => fmtSec(v)} />
            <Tooltip order="series" content={<TimeTooltip timingByLevel={timingByLevel} />} />
            <Legend />
            {groupKeys.map((k) => (
              <Bar
                key={k}
                dataKey={`${k}_median`}
                name={groupLabel(k, boosterN)}
                fill={GROUP_COLORS[k]}
                isAnimationActive={false}
              />
            ))}
            {THRESHOLDS.map((sr) => (
              <Line key={sr.key} type="stepAfter" dataKey={sr.key} name={sr.label} stroke={sr.color} dot={false} isAnimationActive={false} connectNulls />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </section>

      <section>
        <p className="text-sm font-medium mb-2">
          {STAR_SHARE.title}
        </p>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={starShareData} layout="vertical" margin={{ top: 8, right: 16, bottom: 8, left: 16 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} className="text-xs" />
            <YAxis type="category" dataKey="group" className="text-xs" width={100} />
            <Tooltip
              order="series"
              formatter={(value, name, entry) => {
                const idx = name.replace('star', '');
                return [
                  fmtPct(entry.payload[`${name}__num`], entry.payload[`${name}__den`], showAbs),
                  `${idx}★`
                ];
              }}
            />
            <Legend formatter={(value) => `${value.replace('star', '')}★`} />
            {[0, 1, 2, 3].map((i) => (
              <Bar key={i} dataKey={`star${i}`} stackId="stars" fill={STAR_COLORS[i]} isAnimationActive={false} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </section>
    </div>
  );
}

export default AbsorptionTimingCharts;
